import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { SceneGroup } from '@lumen5/luminary';
import type { LexicalEditor } from 'lexical';
import { $createRangeSelection, $getRoot, $setSelection, SELECTION_CHANGE_COMMAND } from 'lexical';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';

import { parseReplacementWithPauses, sameWords } from './pause-tags';
import { distributeWordsAcrossSegments } from './reword-distribution';
import {
    computeSceneGroupShortIds,
    resolveSceneGroupById,
    resolveSceneGroupHandle,
} from './scene-group-addressing';
import {
    $isInCutRegion,
    buildPatternTokens,
    buildTokenStreamFromEditor,
    findTokenSequenceMatches,
    type MatchRange,
    matchRangeForTokens,
    setPausesInRange,
    SPAN_NOT_FOUND_ERROR,
} from './transcript-pause-staging';
import { findAllWordSequenceMatches, type MatchableWord } from './transcript-word-match';
import { VideoStateTool } from './VideoStateTool';
import { AI_VOICEOVER_DISABLED_MESSAGE } from './constants';

import type { AudioAssetSourceData } from 'js/src/client';
import { getCanUseAIVoiceover } from 'js/src/data/account/account-selectors';
import { AI_VOICE_SPEED_OPTIONS, type AIVoiceSpeedName } from 'js/src/data/ai-voices-and-avatars';
import { isSceneGroupLoading } from 'js/src/data/app';
import {
    editAIVoiceoverOperation,
    editFullAIVoiceoverOperation,
    getEditorFromSceneGroupId,
    getFullTranscriptFromEditor,
    getOriginalTranscriptLanguage,
} from 'js/src/data/transcript';
import { $isTranscriptWordNodeAndNotSilence } from 'js/src/data/transcript/nodes/SilenceNode';
import {
    $isTextCorrectElementNode,
    type TextCorrectElementNode,
} from 'js/src/data/transcript/nodes/TextCorrectElementNode';
import type { TranscriptWordNode } from 'js/src/data/transcript/nodes/TranscriptWordNode';
import {
    $createTranscriptWordNode,
    $isTranscriptWordNode,
    EMPTY_WORD_CREATED_BY_TEXT_CORRECTION,
} from 'js/src/data/transcript/nodes/TranscriptWordNode';
import { $getTextCorrectedWindows } from 'js/src/data/transcript/text-correction-windows';
import {
    CANCEL_CORRECTION_COMMAND,
    SAVE_ACTIVE_CORRECTION_COMMAND,
    START_CORRECT_TEXT_COMMAND,
    TextCorrectionTrigger,
} from 'js/src/data/transcript/transcript-types';
import {
    $dfsWithinNode,
    $getOverlayParent,
    $isSpace,
    updateEditor,
} from 'js/src/data/transcript/transcript-utils';
import { refreshFirstScenePreviewOperation } from 'js/src/data/video-planner';
import type { RootState } from 'js/src/libs/RootState';
import uuid from 'js/src/libs/uuid';
import { $deleteAllScenesWithoutWordsOperation } from 'js/src/luminary';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import {
    canSceneGroupUseEditAIVO,
    getVoiceDrivenAssetSourceData,
    getVoiceDrivenBaseAssetCutRegions,
    getVoiceDrivenBaseAssetSourceDuration,
    isAIVoiceoverSceneGroup,
} from 'js/src/luminary/luminary-selectors';

type VoiceSpeed = AIVoiceSpeedName;

// Named speed step → the multiplier the backend stores, sourced from the same canonical
// list the speed selector UI uses.
const VOICE_SPEED_TO_MULTIPLIER = Object.fromEntries(
    AI_VOICE_SPEED_OPTIONS.map((option) => [option.name, option.value]),
) as Record<VoiceSpeed, number>;
const VOICE_SPEED_ENUM = AI_VOICE_SPEED_OPTIONS.map((option) => option.name) as [
    VoiceSpeed,
    ...VoiceSpeed[],
];
const VOICE_SPEED_DESCRIPTION = `A new voice speed. One of: ${AI_VOICE_SPEED_OPTIONS.map(
    (option) => `"${option.name}" (${option.displayRate})`,
).join(', ')}.`;

export const editAIVoiceoverSchema = z.object({
    scene_group_id: z
        .string()
        .describe(
            'The stable scene_group_id of the AI voiceover scene group to edit (from the video state).',
        ),
    find_phrase: z
        .string()
        .optional()
        .describe(
            'A run of consecutive tokens from the current transcript to replace, written exactly as they appear. Must be provided together with replacement.',
        ),
    replacement: z
        .string()
        .optional()
        .describe(
            'The new token string (words and `<pause seconds="N"/>` tags) to put in place of find_phrase, or an empty string to drop find_phrase from the voiceover. Must be provided together with find_phrase.',
        ),
    voice_slug: z
        .string()
        .optional()
        .describe(
            `A new AI voice slug (from the ${TOOL_NAMES.searchVoices} tool) to re-synthesize the voiceover with.`,
        ),
    voice_speed: z.enum(VOICE_SPEED_ENUM).optional().describe(VOICE_SPEED_DESCRIPTION),
});
export type EditAIVoiceoverArgs = z.infer<typeof editAIVoiceoverSchema>;

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * updateEditor, except that an error thrown inside the update rejects. Lexical hands such an
 * error to the editor's onError, which only reports it, and discards the update, so a plain
 * updateEditor resolves as though the update had been applied.
 */
async function updateEditorOrThrow(editor: LexicalEditor, updateFn: () => void): Promise<void> {
    let failure: { error: unknown } | null = null;
    await updateEditor(editor, () => {
        try {
            updateFn();
        } catch (error) {
            failure = { error };
        }
    });
    if (failure) {
        throw (failure as { error: unknown }).error;
    }
}

export class EditAIVoiceoverTool extends VideoStateTool<typeof editAIVoiceoverSchema> {
    protected override readonly schema = editAIVoiceoverSchema;
    protected override readonly toolName = TOOL_NAMES.editAiVoiceover;
    protected override readonly toolDescription = `Edit an existing AI voiceover on a voice-driven scene group. Load the \`${SKILL_NAMES.aiVoiceoverVideoBasics}\` skill for how to use.`;

    public override getToolDisplayName(): string {
        return 'Editing voiceover';
    }

    protected override onEditApplied(args: EditAIVoiceoverArgs): void {
        // Re-synthesizing the first scene group's voiceover can realign its scenes and change the
        // video thumbnail. Resolve defensively — this runs after a successful edit, so it must never
        // throw on an id that no longer resolves.
        const resolved = resolveSceneGroupById(this.luminary, args.scene_group_id);
        if (!('error' in resolved) && resolved.index === 0) {
            this.dispatch(refreshFirstScenePreviewOperation());
        }
    }

    public get statusIcon(): string {
        return 'record_voice_over';
    }

    public async execute(args: EditAIVoiceoverArgs, _context: ToolExecutionContext) {
        if (!getCanUseAIVoiceover(this.getState())) {
            return `Error: ${AI_VOICEOVER_DISABLED_MESSAGE}`;
        }

        const { find_phrase, replacement, voice_slug, voice_speed } = args;

        const { sceneGroup } = resolveSceneGroupHandle(this.luminary, args);
        const shortId =
            computeSceneGroupShortIds(this.luminary.sceneGroups.map((group) => group.id)).get(
                sceneGroup.id,
            ) ?? sceneGroup.id;

        if (!isAIVoiceoverSceneGroup(sceneGroup)) {
            return `Error: Scene group ${shortId} is not an AI voiceover scene group. ${TOOL_NAMES.editAiVoiceover} only works on AI voiceover scene groups, not talking-head or human/recorded voiceover groups.`;
        }

        const wantsTextEdit = find_phrase !== undefined || replacement !== undefined;
        const wantsVoiceChange = voice_slug !== undefined;
        const wantsSpeedChange = voice_speed !== undefined;
        const wantsDrop = wantsTextEdit && replacement?.trim() === '';

        if (!wantsTextEdit && !wantsVoiceChange && !wantsSpeedChange) {
            return 'Error: No edit requested. Provide at least one of: find_phrase + replacement (to reword), voice_slug (to change voice), or voice_speed (to change speed).';
        }
        if (wantsTextEdit && (find_phrase === undefined || replacement === undefined)) {
            return 'Error: find_phrase and replacement must be provided together.';
        }
        if (wantsSpeedChange && VOICE_SPEED_TO_MULTIPLIER[voice_speed] === undefined) {
            return `Error: voice_speed "${voice_speed}" is not allowed. Allowed values are: ${Object.keys(VOICE_SPEED_TO_MULTIPLIER).join(', ')}.`;
        }

        const originalTranscript = sceneGroup.originalTranscript;
        const transcriptWords: MatchableWord[] =
            originalTranscript?.transcriptData?.words || originalTranscript?.words || [];
        if (!originalTranscript || transcriptWords.length === 0) {
            return `Error: Scene group ${shortId} does not have a transcript to edit.`;
        }

        // Validate the phrase against the current transcript up front, before touching the
        // editor or UI, so bad phrases fail cleanly with no side effects. The transcript is one
        // token stream (words + pauses); find_phrase must match one run of it exactly and uniquely.
        let matchRange: MatchRange | null = null;
        if (wantsTextEdit) {
            if (!canSceneGroupUseEditAIVO(sceneGroup)) {
                return `Error: Scene group ${shortId} cannot be reworded because its voiceover predates AI voice metadata. This cannot be fixed by upgrading — do not retry or suggest an upgrade. Voice and speed changes may still work.`;
            }
            const editor = getEditorFromSceneGroupId(this.getState(), sceneGroup.id);
            if (!editor) {
                return `Error: The transcript editor for scene group ${shortId} is not available.`;
            }
            // Cut-aware (see $isInCutRegion) so find_phrase only matches words this group still speaks.
            const stream = buildTokenStreamFromEditor(
                editor,
                getVoiceDrivenBaseAssetCutRegions(sceneGroup),
            );
            const pattern = buildPatternTokens(parseReplacementWithPauses(find_phrase!));
            const matches = findTokenSequenceMatches(stream, pattern);
            if (matches.length === 0) {
                return `Error: The phrase "${find_phrase}" was not found in the transcript. Its words and pauses must appear consecutively exactly as shown — include every <pause/> that falls inside the run you are replacing.`;
            }
            if (matches.length > 1) {
                return `Error: The phrase "${find_phrase}" appears ${matches.length} times in the transcript, so it is ambiguous. Provide a longer, unique phrase.`;
            }
            matchRange = matchRangeForTokens(stream, matches[0]);

            if (wantsDrop) {
                const { words: findWords } = parseReplacementWithPauses(find_phrase!);
                if (findWords.length === 0) {
                    return 'Error: find_phrase has no words to drop. To remove a pause, keep a neighbouring word in both find_phrase and replacement and leave the pause out of the replacement.';
                }
                const spokenWords = stream.filter((token) => token.kind === 'word');
                if (findWords.length === spokenWords.length) {
                    return `Error: find_phrase covers the whole voiceover, so dropping it would leave nothing to say. To remove the narration and its scenes, use ${TOOL_NAMES.deleteSceneGroup}.`;
                }
                // The drop locates its words by their words alone, after removing any pauses between
                // them, so they must be unique without the pauses too.
                const wordOnlyMatches = findAllWordSequenceMatches(
                    spokenWords.map((token) => ({ word: token.text })),
                    [findWords.join(' ')],
                );
                if (wordOnlyMatches.length > 1) {
                    return `Error: The words of "${find_phrase}" appear ${wordOnlyMatches.length} times in the transcript, so they are ambiguous. Provide a longer, unique phrase.`;
                }
            }
        }

        const changeSummary: string[] = [];

        try {
            if (wantsDrop) {
                const { words: findWords, pauses: pausesInFindPhrase } = parseReplacementWithPauses(
                    find_phrase!,
                );
                const sceneCountBefore = sceneGroup.scenes.length;
                if (pausesInFindPhrase.length > 0) {
                    const reconcile = await this.applyPauseReconcile(
                        sceneGroup,
                        findWords,
                        matchRange!,
                        [],
                    );
                    if (reconcile === 'span-not-found') {
                        return `Error: ${SPAN_NOT_FOUND_ERROR}`;
                    }
                }
                await this.applyReword(
                    sceneGroup.id,
                    findWords.join(' '),
                    '',
                    getVoiceDrivenBaseAssetCutRegions(sceneGroup),
                );
                const sceneCountAfter = (
                    this.luminary.getSceneGroupById(sceneGroup.id) ?? sceneGroup
                ).scenes.length;
                const removedScenes = sceneCountBefore - sceneCountAfter;
                changeSummary.push(
                    removedScenes > 0
                        ? `dropped "${find_phrase}", removing ${removedScenes} scene(s) left with nothing to say`
                        : `dropped "${find_phrase}"`,
                );
            } else if (wantsTextEdit) {
                const { words: findWords } = parseReplacementWithPauses(find_phrase!);
                const { words, pauses } = parseReplacementWithPauses(replacement!);
                if (words.length === 0) {
                    return 'Error: replacement must contain words, or be empty to drop find_phrase. To swap words for a pause, keep a neighbouring word in both find_phrase and replacement.';
                }
                const textChanged = !sameWords(words, findWords);

                // Stage 1 — words: reword with the pause tags stripped. Skipped when only the
                // pauses changed, so a pause-only edit doesn't needlessly re-synthesize speech.
                if (textChanged) {
                    await this.applyReword(
                        sceneGroup.id,
                        findWords.join(' '),
                        words.join(' '),
                        getVoiceDrivenBaseAssetCutRegions(sceneGroup),
                    );
                }

                // Stage 2 — pauses: set the silences in the matched (just-reworded) span to
                // match the replacement's tags (add/resize/remove). The span is re-located by its
                // replacement words + the word offset captured during the match, so it survives the
                // reword regenerating word-node ids.
                //
                // Stage 1 has already committed and re-synthesized the reword, so a failure to
                // re-locate the span must not be reported as a failed edit: the words did change.
                // Re-locating can legitimately miss when the segmentation backend re-splits the
                // corrected text, so the replacement's words no longer appear verbatim. A miss
                // mutates nothing, so when the replacement asked for no pauses there is nothing
                // lost and reporting failure would only send the agent into a retry loop over an
                // edit that already landed. A miss when pauses *were* requested still surfaces,
                // since silently dropping them would be wrong.
                const reconcile = await this.applyPauseReconcile(
                    sceneGroup,
                    words,
                    matchRange!,
                    pauses,
                );
                if (reconcile === 'span-not-found' && pauses.length > 0) {
                    return `Error: ${SPAN_NOT_FOUND_ERROR}`;
                }
                const pausesChanged = reconcile === true;

                if (textChanged && pausesChanged) {
                    changeSummary.push(`reworded "${find_phrase}" and adjusted pauses`);
                } else if (textChanged) {
                    changeSummary.push(`reworded "${find_phrase}" → "${words.join(' ')}"`);
                } else if (pausesChanged) {
                    changeSummary.push('adjusted pauses');
                }
            }

            if (wantsVoiceChange || wantsSpeedChange) {
                const newVoiceSpeed = wantsSpeedChange
                    ? VOICE_SPEED_TO_MULTIPLIER[voice_speed]
                    : null;
                const { editFullAIVoiceover } = makeOperationsCallable(
                    { editFullAIVoiceoverOperation },
                    this.luminary,
                    this.dispatch,
                    this.getState as () => RootState,
                );
                await editFullAIVoiceover(sceneGroup.id, newVoiceSpeed, voice_slug);
                if (wantsVoiceChange) {
                    changeSummary.push(`changed voice to "${voice_slug}"`);
                }
                if (wantsSpeedChange) {
                    changeSummary.push(`changed speed to ${voice_speed}`);
                }
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error editing AI voiceover: ${errorMessage}`;
        }

        const editedGroup = this.luminary.getSceneGroupById(sceneGroup.id) ?? sceneGroup;
        const durationMillis = getVoiceDrivenBaseAssetSourceDuration(editedGroup);
        const durationInfo =
            typeof durationMillis === 'number' && durationMillis > 0
                ? ` New voiceover duration: ${(durationMillis / 1000).toFixed(1)}s.`
                : '';

        return `Success: Edited AI voiceover on scene group ${shortId} (${changeSummary.join('; ')}).${durationInfo}`;
    }

    /**
     * Make the pauses inside the matched (just-reworded or unchanged) span match the tags in the
     * replacement, then re-synthesize just that span. Returns whether anything changed — a
     * no-op reconcile (pauses already matched) skips the re-synthesis entirely.
     *
     * Returns `'span-not-found'` rather than throwing when the span cannot be re-located, since
     * that leaves the editor untouched and the caller may be able to treat it as inconsequential.
     * Every other failure throws: those are only reachable once silence nodes have already been
     * added or removed, so the edit must not be reported as a success.
     */
    private async applyPauseReconcile(
        sceneGroup: SceneGroup,
        replacementWords: string[],
        matchRange: MatchRange,
        pauses: ReturnType<typeof parseReplacementWithPauses>['pauses'],
    ): Promise<boolean | 'span-not-found'> {
        const editor = getEditorFromSceneGroupId(this.getState(), sceneGroup.id);
        if (!editor) {
            throw new Error('the transcript editor for this scene group is not available.');
        }

        const parentActionGroup = uuid();
        const { $deleteAllScenesWithoutWords } = makeOperationsCallable(
            { $deleteAllScenesWithoutWordsOperation },
            this.luminary,
            this.dispatch,
            this.getState as () => RootState,
        );

        const result = await setPausesInRange(editor, {
            spanWords: replacementWords,
            wordOffsetHint: matchRange.wordsBefore,
            ownsLeadingGap: matchRange.ownsLeadingGap,
            ownsTrailingGap: matchRange.ownsTrailingGap,
            desiredPauses: pauses,
            onSilenceRemoved: () =>
                $deleteAllScenesWithoutWords(sceneGroup.id, false, parentActionGroup),
            cutRegions: getVoiceDrivenBaseAssetCutRegions(sceneGroup),
        });
        if ('error' in result) {
            return 'span-not-found';
        }
        if (!result.changed) {
            return false;
        }

        const transcript = getFullTranscriptFromEditor(editor, result.userSelectedWordIds, {
            includeSilence: true,
        });
        const { editAIVoiceover } = makeOperationsCallable(
            { editAIVoiceoverOperation },
            this.luminary,
            this.dispatch,
            this.getState as () => RootState,
        );
        await editAIVoiceover(
            {
                sceneGroupId: sceneGroup.id,
                transcript,
                langCode: getOriginalTranscriptLanguage(this.getState(), sceneGroup.id),
                originalVoiceoverAssetSourceData: getVoiceDrivenAssetSourceData(
                    sceneGroup,
                ) as AudioAssetSourceData,
            },
            false,
            parentActionGroup,
        );
        return true;
    }

    /**
     * Reword a phrase by driving the same text-correction flow a human uses in the
     * transcript UI: enter correct mode on the scene group's (already-mounted) editor,
     * replace the matched words, then save — which segments the new text on the backend,
     * splices the new word nodes into the editor, and re-synthesizes the changed span via
     * editAIVoiceoverOperation. Going through the real flow (rather than assembling a
     * request) keeps word ids consistent so overlay/scene reconciliation resolves correctly.
     */
    private async applyReword(
        sceneGroupId: string,
        findPhrase: string,
        replacement: string,
        cutRegions: Array<[number, number]>,
    ): Promise<void> {
        // The Creator keeps the transcript (and its correction plugins) mounted, so we use
        // the live editor directly — no need to open a tab or move the user's focus.
        const editor = getEditorFromSceneGroupId(this.getState(), sceneGroupId);
        if (!editor) {
            throw new Error(
                'the transcript editor for this scene group is not available, so it cannot be reworded.',
            );
        }

        await this.selectWordRun(editor, findPhrase, cutRegions);
        editor.dispatchCommand(START_CORRECT_TEXT_COMMAND, TextCorrectionTrigger.BUTTON);
        try {
            await this.replaceWordsInCorrectionNode(editor, findPhrase, replacement, cutRegions);
        } catch (error) {
            // We entered correction mode but failed before saving; dismiss it so the user
            // isn't left staring at a stuck, half-applied correction in the mounted editor.
            editor.dispatchCommand(CANCEL_CORRECTION_COMMAND, null);
            throw error;
        }
        const voiceoverBeforeSave = this.getVoiceoverSourceKey(sceneGroupId);
        editor.dispatchCommand(SAVE_ACTIVE_CORRECTION_COMMAND, null);

        await this.waitForEditToFinish(sceneGroupId, {
            hasResynthesized: () =>
                this.getVoiceoverSourceKey(sceneGroupId) !== voiceoverBeforeSave,
        });
    }

    /** Changes exactly when the scene group's voiceover audio is replaced by a re-synthesis. */
    private getVoiceoverSourceKey(sceneGroupId: string): string | undefined {
        const sceneGroup = this.luminary.getSceneGroupById(sceneGroupId);
        return sceneGroup ? JSON.stringify(getVoiceDrivenAssetSourceData(sceneGroup)) : undefined;
    }

    /**
     * Ordered, real (non-space, non-silence) transcript word nodes, root-level. Excludes
     * cut-region words so this agrees with the cut-aware up-front validation in `execute` —
     * otherwise a phrase that's unique in the group's active span but repeated in a cut-away
     * span (e.g. a sibling split scene group's words, still present as nodes) would already be
     * confirmed unique there yet found ambiguous here.
     */
    private static wordNodesInOrder(cutRegions: Array<[number, number]>): TranscriptWordNode[] {
        return $getRoot()
            .getAllTextNodes()
            .filter(
                (node): node is TranscriptWordNode =>
                    $isTranscriptWordNode(node) &&
                    $isTranscriptWordNodeAndNotSilence(node) &&
                    !$isSpace(node) &&
                    !$isInCutRegion(node, cutRegions),
            );
    }

    /** Select the matched word run so START_CORRECT_TEXT_COMMAND has a valid range. */
    private async selectWordRun(
        editor: LexicalEditor,
        findPhrase: string,
        cutRegions: Array<[number, number]>,
    ): Promise<void> {
        await updateEditorOrThrow(editor, () => {
            const words = EditAIVoiceoverTool.wordNodesInOrder(cutRegions);
            const matches = findAllWordSequenceMatches(
                words.map((node) => ({ word: node.getTextContent() })),
                [findPhrase],
            );
            if (matches.length !== 1) {
                throw new Error('could not locate the phrase in the transcript editor.');
            }
            const start = words[matches[0].startIndex];
            const end = words[matches[0].endIndex];
            const selection = $createRangeSelection();
            selection.setTextNodeRange(start, 0, end, end.getTextContent().length);
            $setSelection(selection);
        });
        editor.dispatchCommand(SELECTION_CHANGE_COMMAND, undefined);
    }

    /**
     * Replace the matched words inside the active correction node (created by
     * START_CORRECT_TEXT_COMMAND). Same word count: swap word for word. Different word count:
     * group the matched words by scene, share the replacement out per scene, write each
     * scene's share onto its first matched word and remove that scene's other matched words.
     * The save then has the backend re-split and re-time the text. One carrier word per scene
     * keeps every scene populated, since the save deletes scenes left with no words.
     */
    private async replaceWordsInCorrectionNode(
        editor: LexicalEditor,
        findPhrase: string,
        replacement: string,
        cutRegions: Array<[number, number]>,
    ): Promise<void> {
        const replacementWords = replacement.split(/\s+/).filter((word) => word !== '');

        await updateEditorOrThrow(editor, () => {
            const correctionNode = $getRoot().getChildren().find($isTextCorrectElementNode);
            if (!correctionNode) {
                throw new Error(
                    `text correction mode did not start, so the reword could not be applied. ` +
                        `Rebuild the voiceover from the new script instead — ` +
                        `\`${TOOL_NAMES.addVoiceoverSceneGroup}\` for an AI voiceover, ` +
                        `\`${TOOL_NAMES.addAvatarSceneGroup}\` for an avatar.`,
                );
            }
            // For AI-voiceover groups the correction node wraps the WHOLE transcript (see
            // TextCorrectionInteractionPlugin's canUseEditAIVO branch), not just the selection
            // from selectWordRun — so this must exclude cut-region words too, for the same
            // reason selectWordRun does.
            const wordNodes = $dfsWithinNode(correctionNode).filter(
                (node): node is TranscriptWordNode =>
                    $isTranscriptWordNode(node) &&
                    $isTranscriptWordNodeAndNotSilence(node) &&
                    !$isSpace(node) &&
                    !$isInCutRegion(node, cutRegions),
            );
            const matches = findAllWordSequenceMatches(
                wordNodes.map((node) => ({ word: node.getTextContent() })),
                [findPhrase],
            );
            if (matches.length !== 1) {
                throw new Error('could not locate the phrase to replace after entering edit mode.');
            }
            const { startIndex, endIndex } = matches[0];
            const matchedNodes = wordNodes.slice(startIndex, endIndex + 1);

            if (replacementWords.length === 0) {
                EditAIVoiceoverTool.$dropWords(correctionNode, matchedNodes);
                return;
            }
            if (replacementWords.length === matchedNodes.length) {
                matchedNodes.forEach((node, i) => node.setTextContent(replacementWords[i]));
                return;
            }

            const nodesByScene = EditAIVoiceoverTool.groupConsecutiveNodesByScene(matchedNodes);
            if (replacementWords.length < nodesByScene.length) {
                throw new Error(
                    `the phrase spans ${nodesByScene.length} scenes but the replacement has only ${replacementWords.length} word(s), so a scene would be left with nothing to say. Reword one scene at a time, or give every scene at least one word.`,
                );
            }
            const replacementByScene = distributeWordsAcrossSegments(
                replacementWords,
                nodesByScene.map((nodes) => nodes.length),
            );
            nodesByScene.forEach((nodes, i) => {
                nodes[0].setTextContent(replacementByScene[i].join(' '));
                nodes.slice(1).forEach((node) => node.remove());
            });
        });
    }

    /**
     * Delete words the way a user deleting them in correction mode does. Each word goes with a
     * neighbouring space. A pause-bounded window left with no words instead collapses to the empty
     * placeholder word the save uses to tell the backend that stretch was deleted.
     */
    private static $dropWords(
        correctionNode: TextCorrectElementNode,
        droppedNodes: TranscriptWordNode[],
    ): void {
        const droppedKeys = new Set(droppedNodes.map((node) => node.getKey()));
        const nodesLeftToDrop = new Set(droppedNodes);
        $getTextCorrectedWindows(correctionNode).forEach((window) => {
            const windowNodes = window.filter($isTranscriptWordNode);
            const windowWords = windowNodes.filter((node): boolean => !$isSpace(node));
            const droppedWords = windowWords.filter((node) => droppedKeys.has(node.getKey()));
            if (droppedWords.length === 0 || droppedWords.length < windowWords.length) {
                return;
            }
            const placeholder = $createTranscriptWordNode({
                text: ' ',
                startTime: droppedWords[0].startTime,
                endTime: droppedWords[droppedWords.length - 1].endTime,
                isFiller: false,
            });
            placeholder.className = EMPTY_WORD_CREATED_BY_TEXT_CORRECTION;
            droppedWords[0].insertBefore(placeholder);
            windowNodes.forEach((node) => node.remove());
            droppedWords.forEach((node) => nodesLeftToDrop.delete(node));
        });
        nodesLeftToDrop.forEach((node) => {
            const next = node.getNextSibling();
            const previous = node.getPreviousSibling();
            if ($isSpace(next)) {
                next.remove();
            } else if ($isSpace(previous)) {
                previous.remove();
            }
            node.remove();
        });
    }

    /**
     * Split an array of consecutive word nodes into one group per scene. Overlay words are wrapped in an
     * overlay node; base-scene words sit directly in the container, and base-scene words on
     * either side of an overlay are different scenes.
     */
    private static groupConsecutiveNodesByScene(
        nodes: TranscriptWordNode[],
    ): TranscriptWordNode[][] {
        const groups: TranscriptWordNode[][] = [];
        let previousContainerKey: string | undefined;
        for (const node of nodes) {
            const containerKey = ($getOverlayParent(node) ?? node.getParentOrThrow()).getKey();
            if (containerKey !== previousContainerKey) {
                groups.push([]);
                previousContainerKey = containerKey;
            }
            groups[groups.length - 1].push(node);
        }
        return groups;
    }

    /**
     * Wait for the re-synthesis kicked off by saving the correction to complete. Saving
     * first calls the text-segmentation backend, then editAIVoiceoverOperation marks the
     * scene group loading and clears it when reconciliation finishes.
     *
     * Pass `hasResynthesized` when the caller cannot otherwise tell whether a re-synthesis
     * happened: a save that never starts one did not apply the edit, and must fail rather than
     * fall through. It is checked alongside the loading flag because a re-synthesis that
     * completes between two polls never shows as loading.
     */
    private async waitForEditToFinish(
        sceneGroupId: string,
        { hasResynthesized }: { hasResynthesized?: () => boolean } = {},
    ): Promise<void> {
        // Wait for loading to begin (segmentation runs first, so this isn't immediate).
        let started = false;
        for (let attempt = 0; attempt < 200; attempt++) {
            if (isSceneGroupLoading(this.getState(), sceneGroupId)) {
                started = true;
                break;
            }
            if (hasResynthesized?.()) {
                return;
            }
            await delay(25);
        }
        if (hasResynthesized && !started && !hasResynthesized()) {
            throw new Error(
                'the voiceover re-synthesis never started, so the edit was not applied.',
            );
        }
        // Wait for reconciliation + checkpoint to finish.
        for (let attempt = 0; attempt < 2400; attempt++) {
            if (!isSceneGroupLoading(this.getState(), sceneGroupId)) {
                return;
            }
            await delay(25);
        }
        throw new Error('the voiceover re-synthesis did not finish in time.');
    }
}
